import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useDeleteSmsTemplateMutation,
  useGetSmsTemplateWithLimitOffsetMutation,
  useUpdateSmsTemplateStatusMutation,
  useAddSmsTemplateMutation,
  useUpdateSmsTemplateMutation,
} from "../../../features/admin/messageTemplate/messageTemplateApi";
import { toast } from "react-hot-toast";
import { HiPlus, HiArrowLeft, HiPencil, HiTrash } from "react-icons/hi";
import { MdSms } from "react-icons/md";
import { Drawer, Button } from "../../../components/ui";

const PAGE_SIZE = 10;

const TEMPLATE_TYPES = [
  "otp", "registration", "block account", "activate account", "reset",
  "profile completion", "set mpin", "redeem request", "bonus points",
  "birthday wishes", "redemption reminders", "scan_coupon points",
  "complaint message", "gift delivery", "extra points", "account status",
  "point earned", "gift redeem request",
];

const StatusBadge = ({ status, onClick }) => {
  const active = status !== "2";
  return (
    <button onClick={onClick} style={{
      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, cursor: "pointer", border: "none",
      background: active ? "#d1fae5" : "#f1f5f9",
      color: active ? "#059669" : "#64748b",
    }}>
      {active ? "Active" : "Inactive"}
    </button>
  );
};

const emptyForm = { type: "", templateId: "", text: "", senderId: "" };

export default function MessageTemplate() {
  const navigate = useNavigate();
  const location = useLocation();
  const credential = location.state || {};

  const [limit] = useState(PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // drawer state
  const [drawer, setDrawer] = useState(null); // null | { mode: 'add' } | { mode: 'edit', row }
  const [form, setForm] = useState(emptyForm);

  const [getSmsTemplates, { data, isLoading }] = useGetSmsTemplateWithLimitOffsetMutation();
  const [deleteSmsTemplate, { data: deleteData }] = useDeleteSmsTemplateMutation();
  const [updateStatus, { data: statusData }] = useUpdateSmsTemplateStatusMutation();
  const [addSmsTemplate, { isLoading: adding }] = useAddSmsTemplateMutation();
  const [updateSmsTemplate, { isLoading: updating }] = useUpdateSmsTemplateMutation();

  const token = () => sessionStorage.getItem("access_token");

  const fetch = (off = offset) => {
    getSmsTemplates({ token: token(), account_id: credential.id, limit, offset: off });
  };

  useEffect(() => { fetch(); }, [offset]);

  useEffect(() => {
    if (deleteData) { toast.success(deleteData.message); fetch(); }
  }, [deleteData]);

  useEffect(() => {
    if (statusData) { toast.success(statusData.message); fetch(); }
  }, [statusData]);

  const handleDelete = (row) => {
    if (!window.confirm(`Delete template "${row.template_id}"?`)) return;
    deleteSmsTemplate({ token: token(), id: Number(row.id) });
  };

  const handleStatusToggle = (row) => {
    updateStatus({ token: token(), id: Number(row.id) });
  };

  const openAdd = () => {
    setForm(emptyForm);
    setDrawer({ mode: "add" });
  };

  const openEdit = (row) => {
    setForm({
      type: row.type || "",
      templateId: String(row.template_id || ""),
      text: row.template_text || "",
      senderId: row.sender_id || "",
    });
    setDrawer({ mode: "edit", row });
  };

  const closeDrawer = () => setDrawer(null);

  const handleSave = async () => {
    if (!form.type)       { toast.error("Select a template type");  return; }
    if (!form.templateId) { toast.error("Enter a template ID");     return; }
    if (!form.text)       { toast.error("Enter the template text"); return; }
    if (!form.senderId)   { toast.error("Enter a sender ID");       return; }

    const body = { template_id: form.templateId, template_text: form.text, sender_id: form.senderId, type: form.type };

    try {
      if (drawer.mode === "edit") {
        const res = await updateSmsTemplate({ token: token(), id: drawer.row.id, body }).unwrap();
        toast.success(res.message || "Template updated");
      } else {
        const res = await addSmsTemplate({
          token: token(),
          body: { ...body, account_id: Number(credential.id), template_owned_by_id: credential.tenant_id },
        }).unwrap();
        toast.success(res.message || "Template added");
      }
      closeDrawer();
      fetch();
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  const rows = data?.body?.data || [];
  const total = data?.body?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const busy = adding || updating;

  const truncate = (str, n = 60) => str && str.length > n ? str.slice(0, n) + "…" : str;

  const inp = { border: "1px solid #e2e8f0", borderRadius: "8px", padding: "9px 12px", width: "100%", fontSize: "14px", outline: "none", background: "#fff", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };
  const field = { marginBottom: "18px" };

  const COLS = ["#", "Template ID", "Message", "Sender ID", "Type", "Created", "Status", ""];

  return (
    <div className="page-content">
      <div style={{ padding: "24px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button onClick={() => navigate(-1)}
              style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "7px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: "#374151" }}>
              <HiArrowLeft size={14} /> Back
            </button>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <MdSms size={18} style={{ color: "#6366f1" }} />
                <h4 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#0f172a" }}>SMS Templates</h4>
              </div>
              {credential.type && (
                <p style={{ margin: 0, fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  Provider: <strong>{credential.type}</strong>
                  {credential.entity_id ? ` · Entity: ${credential.entity_id}` : ""}
                </p>
              )}
            </div>
          </div>
          <button onClick={openAdd}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
            <HiPlus size={16} /> Add Template
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {COLS.map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>Loading templates…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>
                    <MdSms size={32} style={{ marginBottom: "8px", opacity: 0.3 }} />
                    <div>No templates found. Add one to get started.</div>
                  </td>
                </tr>
              ) : rows.map((r, idx) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "12px 16px", color: "#94a3b8", fontSize: "12px" }}>{offset + idx + 1}</td>
                  <td style={{ padding: "12px 16px", fontFamily: "monospace", color: "#374151", fontSize: "12px" }}>{r.template_id || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "#374151", maxWidth: "280px" }}>
                    <span title={r.template_text}>{truncate(r.template_text)}</span>
                  </td>
                  <td style={{ padding: "12px 16px", color: "#374151" }}>{r.sender_id || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {r.type && (
                      <span style={{ padding: "2px 8px", background: "#ede9fe", color: "#5b21b6", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }}>
                        {r.type}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", color: "#64748b", whiteSpace: "nowrap", fontSize: "12px" }}>
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <StatusBadge status={r.status} onClick={() => handleStatusToggle(r)} />
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => openEdit(r)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", color: "#374151", cursor: "pointer" }}>
                        <HiPencil size={12} /> Edit
                      </button>
                      <button onClick={() => handleDelete(r)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "6px", fontSize: "12px", color: "#e11d48", cursor: "pointer" }}>
                        <HiTrash size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button disabled={currentPage === 1}
                  onClick={() => { const p = currentPage - 1; setCurrentPage(p); setOffset((p - 1) * limit); }}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === 1 ? "#f8fafc" : "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: "12px", color: "#374151" }}>
                  Previous
                </button>
                <button disabled={currentPage === totalPages}
                  onClick={() => { const p = currentPage + 1; setCurrentPage(p); setOffset((p - 1) * limit); }}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === totalPages ? "#f8fafc" : "#fff", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: "12px", color: "#374151" }}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Add / Edit Drawer */}
      {drawer && (
        <Drawer
          width="md"
          title={drawer.mode === "add" ? "Add SMS Template" : "Edit Template"}
          onClose={closeDrawer}
          footer={
            <>
              <Button variant="outline" onClick={closeDrawer}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={busy}>
                {busy ? "Saving…" : drawer.mode === "add" ? "Add Template" : "Update Template"}
              </Button>
            </>
          }
        >
          <div style={{ padding: "24px" }}>
            {drawer.mode === "edit" && drawer.row?.id && (
              <p style={{ margin: "0 0 20px", fontSize: "12px", color: "#64748b" }}>ID: {drawer.row.id}</p>
            )}

            <div style={field}>
              <label style={lbl}>Template Type *</label>
              <select style={inp} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="">— Select type —</option>
                {TEMPLATE_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={field}>
              <label style={lbl}>Template ID *</label>
              <input style={inp} type="text" value={form.templateId}
                placeholder="e.g. 1234567890123456789"
                onChange={e => setForm(p => ({ ...p, templateId: e.target.value }))} />
            </div>

            <div style={field}>
              <label style={lbl}>Template Text *</label>
              <textarea style={{ ...inp, height: "120px", resize: "vertical" }} value={form.text}
                placeholder="Enter SMS template message text…"
                onChange={e => setForm(p => ({ ...p, text: e.target.value }))} />
              <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8" }}>{form.text.length} chars</p>
            </div>

            <div style={field}>
              <label style={lbl}>Sender ID *</label>
              <input style={inp} type="text" value={form.senderId}
                placeholder="e.g. GENIFY"
                onChange={e => setForm(p => ({ ...p, senderId: e.target.value }))} />
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
}
